<script setup lang="ts">
import { marked } from 'marked'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { logService, LOG_CATEGORIES } from '@/services/logService'

interface Props {
  htmlContent: string
  isLoading?: boolean
}

const props = defineProps<Props>()

const stripH1 = (html: string): string => {
  const container = document.createElement('div')
  container.innerHTML = html
  container.querySelectorAll('h1').forEach((el) => el.remove())
  return container.innerHTML
}

const renderedHtml = computed(() => {
  const content = props.htmlContent || ''
  const looksLikeHtml = /<[^>]+>/.test(content)
  const html = looksLikeHtml ? content : (marked.parse(content) as string)
  return stripH1(html)
})

const contentRef = ref<HTMLDivElement | null>(null)

const processLinks = () => {
  const container = contentRef.value
  if (!container) return

  try {
    const links = container.querySelectorAll<HTMLAnchorElement>('a')
    links.forEach((link) => {
      if (!link.className.includes('hover:underline')) {
        link.className = `${link.className} hover:underline`.trim()
      }

      const href = link.getAttribute('href')
      if (!href) return

      const isExternal = href.startsWith('http') || href.startsWith('//') || href.startsWith('www.')
      const isMailto = href.startsWith('mailto:')

      if (isExternal && !isMailto) {
        link.setAttribute('target', '_blank')
        link.setAttribute('rel', 'noopener noreferrer')
      }
    })
  } catch (error) {
    logService.warn(LOG_CATEGORIES.UI, 'Static page link processing failed', { error })
  }
}

const decodeObfuscatedEmails = () => {
  const container = contentRef.value
  if (!container) return

  try {
    const nodes = container.querySelectorAll<HTMLElement>('.obfuscated-email[data-encoded]')
    nodes.forEach((node) => {
      const encoded = node.getAttribute('data-encoded') || ''
      let padded = encoded
      while (padded.length % 4 !== 0) padded += '='

      let email = ''
      try {
        email = atob(padded)
      } catch (error) {
        logService.warn(LOG_CATEGORIES.UI, 'Failed to decode obfuscated email', {
          error,
          encoded,
        })
        return
      }

      const a = document.createElement('a')
      a.href = `mailto:${email}`
      a.textContent = email
      a.rel = 'noopener noreferrer'
      a.className = 'text-primary-600 hover:underline break-all'
      node.replaceWith(a)
    })
  } catch (error) {
    logService.warn(LOG_CATEGORIES.UI, 'Static page email decoding failed', { error })
  }
}

watch(
  renderedHtml,
  async () => {
    await nextTick()
    processLinks()
    decodeObfuscatedEmails()
  },
  { flush: 'post', immediate: true },
)

onMounted(async () => {
  await nextTick()
  processLinks()
  decodeObfuscatedEmails()
})
</script>

<template>
  <div class="flex-1 px-5 py-10 sm:px-8 sm:py-12">
    <div class="mx-auto max-w-5xl">
      <div v-if="isLoading" class="w-full flex items-center justify-center py-12">
        <div class="flex items-center space-x-3 text-gray-500">
          <div
            class="w-5 h-5 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin"
          />
          <span class="text-sm">Loading…</span>
        </div>
      </div>
      <div
        v-else
        ref="contentRef"
        class="max-w-4xl text-gray-800 leading-relaxed text-sm sm:text-base space-y-4 [&>h1]:text-xl [&>h1]:font-semibold [&>h2]:text-lg [&>h2]:font-semibold [&>h3]:text-base [&>h3]:font-semibold [&>pre]:bg-gray-100 [&>pre]:p-3 [&>pre]:rounded-md [&>p]:mb-3 [&_a]:text-primary-600 [&_a:hover]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_table]:w-full [&_table]:border-collapse [&_table]:overflow-hidden [&_table]:rounded-lg [&_table]:border [&_table]:border-gray-200 [&_thead]:bg-gray-50 [&_th]:border [&_th]:border-gray-200 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_td]:border [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-2 [&_kbd]:inline-flex [&_kbd]:min-w-[1.5rem] [&_kbd]:justify-center [&_kbd]:rounded-md [&_kbd]:border [&_kbd]:border-gray-300 [&_kbd]:bg-gray-50 [&_kbd]:px-2 [&_kbd]:py-0.5 [&_kbd]:font-mono [&_kbd]:text-xs [&_kbd]:font-semibold"
        v-html="renderedHtml"
      />
    </div>
  </div>
</template>
